import { Box, Typography, IconButton, useTheme, useMediaQuery, Slide } from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ExoAvatar from '../exo/ExoAvatar';
import CloseIcon from '@mui/icons-material/Close';
import { useChatClient } from './useChatClient';
import { useTranslation } from 'react-i18next';

export const ChatPopupNotification = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { notifications, dismissNotification } = useChatClient();
  const [visibleNotifications, setVisibleNotifications] = useState([]);

  // Don't show notifications when user is on the chat page
  const isOnChatPage = location.pathname.startsWith('/chat');

  useEffect(() => {
    if (isOnChatPage) {
      setVisibleNotifications([]);
      return;
    }
    // Show fewer notifications on mobile
    setVisibleNotifications(notifications.slice(0, isMobile ? 2 : 3));
  }, [notifications, isMobile, isOnChatPage]);

  const handleClick = (notification) => {
    navigate(`/chat?conversation=${notification.conversationId}`);
    dismissNotification(notification.id);
  };

  const handleDismiss = (e, notificationId) => {
    e.stopPropagation();
    dismissNotification(notificationId);
  };

  if (visibleNotifications.length === 0) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: isMobile ? 60 : 70,
        right: isMobile ? 8 : 20,
        left: isMobile ? 8 : 'auto',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      {visibleNotifications.map((notification) => (
        <Slide
          key={notification.id}
          direction="left"
          in={true}
          mountOnEnter
          unmountOnExit
          timeout={{ enter: 300, exit: 200 }}
        >
          <Box
            onClick={() => handleClick(notification)}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              p: isMobile ? 1.5 : 2,
              backgroundColor: colors.glass,
              backdropFilter: 'blur(12px)',
              borderRadius: '16px',
              boxShadow: `0 4px 24px ${colors.shadow || 'rgba(0, 0, 0, 0.25)'}`,
              border: `1px solid ${colors.card}`,
              cursor: 'pointer',
              minWidth: isMobile ? 'auto' : 320,
              maxWidth: isMobile ? '100%' : 400,
              width: isMobile ? '100%' : 'auto',
              transition: 'transform 0.2s ease, background-color 0.2s ease',
              '&:hover': {
                transform: isMobile ? 'none' : 'translateX(-4px)',
                backgroundColor: colors.card,
              },
              '&:active': {
                transform: 'scale(0.98)',
              },
            }}
          >
            <ExoAvatar
              size={isMobile ? '36px' : '40px'}
              name={notification.senderName}
              picture={notification.senderAvatar}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box className="flex items-center justify-between gap-2">
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: colors.text,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontSize: isMobile ? '0.85rem' : '0.875rem',
                  }}
                >
                  {notification.senderName}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => handleDismiss(e, notification.id)}
                  sx={{
                    p: isMobile ? 0.5 : 0.25,
                    color: colors.text,
                    opacity: 0.5,
                    '&:hover': { opacity: 1 },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
              {notification.conversationName && (
                <Typography
                  variant="caption"
                  sx={{ color: colors.info, display: 'block', fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                >
                  {t('in')} {notification.conversationName}
                </Typography>
              )}
              <Typography
                variant="body2"
                sx={{
                  color: colors.text,
                  opacity: 0.7,
                  mt: 0.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: isMobile ? 1 : 2,
                  WebkitBoxOrient: 'vertical',
                  fontSize: isMobile ? '0.8rem' : '0.875rem',
                }}
              >
                {notification.content}
              </Typography>
            </Box>
          </Box>
        </Slide>
      ))}
    </Box>
  );
};
